import {
  Avatar,
  AvatarFallback,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import type { ColumnDef } from "@tanstack/react-table";
import { Calendar } from "lucide-react";
import type { Invitation } from "@/app/invitations/types";
import { RoleBadge } from "@/app/roles/components/role-badge";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { DeleteInvitationButton } from "./delete-invitation-button";

export const getProjectInvitationsColumns = (
  showRole: boolean,
  canDelete: boolean
): ColumnDef<Invitation>[] => [
  {
    id: "invitation_id",
    accessorKey: "invitation_id",
    enableHiding: true,
    meta: {
      displayName: "Invitation ID",
      defaultVisible: false,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invitation ID" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const invitationId = row.getValue("invitation_id") as string;
      const shortId = `${invitationId.slice(0, 8)}...${invitationId.slice(-4)}`;

      return (
        <div className="flex items-center gap-2">
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {shortId}
          </code>
          <CopyButton
            className="h-7 w-7"
            content={invitationId}
            size="icon"
            variant="ghost"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "invitee_email",
    id: "invitee_email",
    meta: {
      displayName: "Invitee Email",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invitee Email" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const email = row.getValue("invitee_email") as string;
      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 border">
            <AvatarFallback className="text-[10px]">
              {email.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-sm">{email}</p>
          </div>
        </div>
      );
    },
    filterFn: (row, _, value) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const email = row.getValue("invitee_email") as string;
      return email.toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "expires_at",
    id: "expires_at",
    meta: {
      displayName: "Expires",
      defaultVisible: true,
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Expires" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const expiresAt = row.getValue("expires_at") as string;
      if (!expiresAt) {
        return "—";
      }

      return (
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <Badge className="font-normal" variant="secondary">
            {formatExpirationDate(expiresAt)}
          </Badge>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const dateA = new Date(rowA.getValue("expires_at") as string).getTime();
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const dateB = new Date(rowB.getValue("expires_at") as string).getTime();
      return dateA - dateB;
    },
  },
  ...(showRole
    ? [
        {
          id: "role",
          accessorFn: (row: Invitation) => row.role?.display_name ?? "",
          meta: {
            displayName: "Role",
            defaultVisible: true,
          },
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Role" />
          ),
          cell: ({ row }) =>
            row.original.role ? <RoleBadge role={row.original.role} /> : "—",
        } satisfies ColumnDef<Invitation>,
      ]
    : []),
  // Delete is the only row action, so omit the column entirely when the caller
  // cannot delete invitations rather than render an empty column.
  ...(canDelete
    ? [
        {
          id: "actions",
          meta: {
            displayName: "Actions",
            defaultVisible: true,
          },
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Actions" />
          ),
          cell: ({ row }) => (
            <DeleteInvitationButton
              invitationId={row.original.invitation_id}
              inviteeEmail={row.original.invitee_email}
            />
          ),
        } satisfies ColumnDef<Invitation>,
      ]
    : []),
];

const formatExpirationDate = (date: string) => {
  const expirationDate = new Date(date);

  // Format: "Dec 25, 3:45 PM"
  const dateFormat = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return dateFormat.format(expirationDate);
};
